import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { ORDER_WHATSAPP_NUMBER, DELIVERY_TIERS, money } from "../data";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { toast } from "sonner";

const buildItemLines = (item) => {
  const lines = [`${item.qty} x ${item.name} — ${money(item.unitPrice * item.qty)}`];
  if (item.prep) lines.push(`   Preparación: ${item.prep}`);
  if (item.selectedChoices)
    item.selectedChoices.forEach((c) =>
      lines.push(`   ${c.title}: ${c.value}`),
    );
  if (item.salsa && item.salsa.length)
    lines.push(`   Salsa: ${item.salsa.join(", ")}`);
  if (item.crema) lines.push(`   ${item.crema}`);
  if (item.queso) lines.push(`   ${item.queso}`);
  if (item.type === "individual" || item.type === "chilaquiles" || item.type === "granel")
    lines.push(`   Verdura: aparte`);
  if (item.extras && item.extras.length)
    item.extras.forEach((e) =>
      lines.push(`   + ${e.name} x${e.qty} (+${money(e.price * e.qty)})`),
    );
  if (item.notes) lines.push(`   Notas: ${item.notes}`);
  return lines.join("\n");
};

const buildMessage = ({ items, totalAmount, comments, form }) => {
  const parts = [];
  parts.push("NUEVO PEDIDO — LA HERENCIA CASTAÑÓN");
  parts.push("");
  parts.push(`Cliente: ${form.name}`);
  parts.push(`Teléfono: ${form.phone}`);
  parts.push("");
  parts.push("PEDIDO:");
  items.forEach((it) => parts.push(buildItemLines(it)));
  parts.push("");
  parts.push(`TOTAL: ${money(totalAmount)}`);
  parts.push("");
  parts.push("TIPO DE PEDIDO:");
  if (form.tipo === "domicilio") {
    const tier = DELIVERY_TIERS.find((t) => t.id === form.distancia);
    parts.push("Entrega a domicilio");
    parts.push(`Dirección: ${form.direccion}`);
    if (form.referencias.trim())
      parts.push(`Referencias: ${form.referencias}`);
    if (tier) parts.push(`Distancia aproximada: ${tier.label}`);
  } else {
    parts.push("Recoger en el restaurante");
    if (form.hora) parts.push(`Hora de recolección: ${form.hora}`);
  }
  parts.push("");
  parts.push(`Comentarios: ${comments ? comments : "—"}`);
  if (form.tipo === "domicilio") {
    const tier = DELIVERY_TIERS.find((t) => t.id === form.distancia);
    parts.push("");
    parts.push("Servicio a domicilio: sin costo de envío.");
    if (tier && tier.min === null)
      parts.push(
        "Nota: entrega a más de 10 km, sujeta a confirmación de disponibilidad por WhatsApp.",
      );
  }
  return parts.join("\n");
};

const ItemRow = ({ item }) => {
  const { incQty, decQty, removeItem } = useCart();
  return (
    <div
      data-testid="cart-item"
      className="flex gap-3 border-b border-brand-gold/20 py-4"
    >
      <div className="flex-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-sans font-bold text-brand-burgundy leading-tight text-base">
            {item.name}
          </h4>
          <span className="font-semibold text-brand-burgundy whitespace-nowrap">
            {money(item.unitPrice * item.qty)}
          </span>
        </div>
        <div className="text-xs text-brand-burgundy/70 mt-1 space-y-0.5">
          {item.prep && <p>Preparación: {item.prep}</p>}
          {item.selectedChoices &&
            item.selectedChoices.map((c) => (
              <p key={c.title}>
                {c.title}: {c.value}
              </p>
            ))}
          {item.salsa && item.salsa.length > 0 && (
            <p>Salsa: {item.salsa.join(", ")}</p>
          )}
          {item.crema && <p>{item.crema}</p>}
          {item.queso && <p>{item.queso}</p>}
          {(item.type === "individual" ||
            item.type === "chilaquiles" ||
            item.type === "granel") && <p>Verdura: aparte</p>}
          {item.extras &&
            item.extras.map((e) => (
              <p key={e.name}>
                + {e.name} x{e.qty} (+{money(e.price * e.qty)})
              </p>
            ))}
          {item.notes && <p className="italic">“{item.notes}”</p>}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => decQty(item.lineId)}
              aria-label="Disminuir"
              data-testid="cart-dec-btn"
              className="w-8 h-8 flex items-center justify-center rounded-full border border-brand-burgundy/30 text-brand-burgundy"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-5 text-center font-semibold text-brand-burgundy">
              {item.qty}
            </span>
            <button
              onClick={() => incQty(item.lineId)}
              aria-label="Aumentar"
              data-testid="cart-inc-btn"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-burgundy text-brand-cream"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => removeItem(item.lineId)}
            aria-label="Eliminar"
            data-testid="cart-remove-btn"
            className="text-brand-red hover:text-brand-burgundy flex items-center gap-1 text-xs font-semibold"
          >
            <Trash2 className="w-4 h-4" /> Quitar
          </button>
        </div>
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    totalAmount,
    totalCount,
    comments,
    setComments,
    clearCart,
  } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    tipo: "recoger",
    direccion: "",
    referencias: "",
    distancia: "",
    hora: "",
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const tier = DELIVERY_TIERS.find((t) => t.id === form.distancia);
  const minRequired = form.tipo === "domicilio" && tier ? tier.min : null;
  const meetsMin = minRequired == null ? true : totalAmount >= minRequired;
  const missing = minRequired == null ? 0 : Math.max(0, minRequired - totalAmount);

  const canSubmit =
    form.name.trim() &&
    form.phone.trim() &&
    (form.tipo === "recoger" || (form.direccion.trim() && meetsMin));

  const handleSend = () => {
    if (form.tipo === "domicilio" && !meetsMin) {
      toast.error(
        `Te faltan ${money(missing)} para el pedido mínimo a domicilio`,
      );
      return;
    }
    if (!canSubmit) {
      toast.error("Completa los datos obligatorios");
      return;
    }
    const msg = buildMessage({ items, totalAmount, comments, form });
    const url = `https://wa.me/${ORDER_WHATSAPP_NUMBER}?text=${encodeURIComponent(
      msg,
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp con tu pedido…");
  };

  const close = () => {
    closeCart();
    setStep("cart");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60"
          onClick={close}
          data-testid="cart-overlay"
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
            className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-brand-cream flex flex-col"
          >
            <header className="bg-brand-burgundy text-brand-cream px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {step === "checkout" && (
                  <button
                    onClick={() => setStep("cart")}
                    aria-label="Regresar"
                    className="mr-1"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <ShoppingBag className="w-5 h-5 text-brand-gold" />
                <h3 className="font-display text-xl font-bold">
                  {step === "cart" ? "Tu pedido" : "Tus datos"}
                </h3>
              </div>
              <button onClick={close} aria-label="Cerrar carrito" data-testid="cart-close-btn">
                <X className="w-6 h-6" />
              </button>
            </header>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8 text-brand-burgundy/70">
                <ShoppingBag className="w-14 h-14 text-brand-gold/50 mb-4" />
                <p className="font-display text-xl font-bold text-brand-burgundy">
                  Tu carrito está vacío
                </p>
                <p className="text-sm mt-2">
                  Agrega platillos desde el menú para comenzar tu pedido.
                </p>
                <button
                  onClick={close}
                  className="mt-6 border border-brand-burgundy text-brand-burgundy font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-full"
                >
                  Ver menú
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5">
                  {step === "cart" ? (
                    <>
                      {items.map((it) => (
                        <ItemRow key={it.lineId} item={it} />
                      ))}
                      <div className="py-5">
                        <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-2 block">
                          Comentarios adicionales
                        </label>
                        <textarea
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          data-testid="cart-comments"
                          rows={2}
                          placeholder="Algo más que debamos saber sobre tu pedido"
                          className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-sm text-brand-burgundy placeholder:text-brand-burgundy/40 focus:outline-none focus:border-brand-gold bg-white"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="py-5 space-y-4">
                      <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-1.5 block">
                          Nombre *
                        </label>
                        <input
                          value={form.name}
                          onChange={upd("name")}
                          data-testid="checkout-name"
                          className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy focus:outline-none focus:border-brand-gold bg-white"
                          placeholder="Tu nombre"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-1.5 block">
                          Teléfono *
                        </label>
                        <input
                          value={form.phone}
                          onChange={upd("phone")}
                          data-testid="checkout-phone"
                          inputMode="tel"
                          className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy focus:outline-none focus:border-brand-gold bg-white"
                          placeholder="614 000 0000"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-2 block">
                          Tipo de pedido *
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { v: "recoger", l: "Recoger" },
                            { v: "domicilio", l: "A domicilio" },
                          ].map((o) => (
                            <button
                              key={o.v}
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, tipo: o.v }))}
                              data-testid={`tipo-${o.v}`}
                              translate="no"
                              className={`py-3 rounded-lg font-bold text-sm border transition-colors ${
                                form.tipo === o.v
                                  ? "bg-brand-burgundy text-brand-cream border-brand-burgundy"
                                  : "bg-white text-brand-burgundy border-brand-gold/50"
                              }`}
                            >
                              {o.l}
                            </button>
                          ))}
                        </div>
                      </div>
                      {form.tipo === "recoger" && (
                        <div>
                          <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-1.5 block">
                            Hora de recolección (opcional)
                          </label>
                          <input
                            type="time"
                            value={form.hora}
                            onChange={upd("hora")}
                            data-testid="checkout-pickup-time"
                            className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy bg-white focus:outline-none focus:border-brand-gold"
                          />
                        </div>
                      )}

                      {form.tipo === "domicilio" && (
                        <>
                          <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-1.5 block">
                              Dirección *
                            </label>
                            <input
                              value={form.direccion}
                              onChange={upd("direccion")}
                              data-testid="checkout-address"
                              className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy focus:outline-none focus:border-brand-gold bg-white"
                              placeholder="Calle, número, colonia"
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-1.5 block">
                              Referencias / instrucciones
                            </label>
                            <textarea
                              value={form.referencias}
                              onChange={upd("referencias")}
                              data-testid="checkout-references"
                              rows={2}
                              className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy focus:outline-none focus:border-brand-gold bg-white"
                              placeholder="Entre calles, color de casa, etc."
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-2 block">
                              Distancia aproximada
                            </label>
                            <select
                              value={form.distancia}
                              onChange={upd("distancia")}
                              data-testid="checkout-distance"
                              className="w-full border border-brand-gold/40 rounded-lg px-4 py-3 text-brand-burgundy bg-white focus:outline-none focus:border-brand-gold"
                            >
                              <option value="">Selecciona tu distancia (opcional)</option>
                              {DELIVERY_TIERS.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.label}
                                  {t.min ? ` · mínimo ${money(t.min)}` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                          {form.distancia === "" ? (
                            <p
                              data-testid="delivery-hint"
                              className="text-xs text-brand-burgundy/70 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-4 py-3"
                            >
                              Opcional: indícanos tu distancia para conocer el
                              pedido mínimo. Servicio a domicilio sin costo de
                              envío.
                            </p>
                          ) : minRequired == null ? (
                            <p
                              data-testid="delivery-notice"
                              className="text-xs text-brand-burgundy/80 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-4 py-3"
                            >
                              Entregas a más de 10 km están sujetas a
                              confirmación de disponibilidad por WhatsApp.
                              Servicio a domicilio sin costo de envío.
                            </p>
                          ) : !meetsMin ? (
                            <p
                              data-testid="delivery-min-warning"
                              className="text-xs font-semibold text-brand-red bg-brand-red/10 border border-brand-red/40 rounded-lg px-4 py-3"
                            >
                              Te faltan {money(missing)} para alcanzar el pedido
                              mínimo de {money(minRequired)} para tu distancia (
                              {tier.label}).
                            </p>
                          ) : (
                            <p className="text-xs text-brand-burgundy/70 bg-brand-gold/10 border border-brand-gold/30 rounded-lg px-4 py-3">
                              ¡Listo! Cumples el pedido mínimo. Servicio a
                              domicilio sin costo de envío.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <footer className="border-t border-brand-gold/30 px-5 py-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-brand-burgundy/70 text-sm">
                      Total ({totalCount} art.)
                    </span>
                    <span
                      data-testid="cart-total"
                      className="font-display text-2xl font-bold text-brand-burgundy"
                    >
                      {money(totalAmount)}
                    </span>
                  </div>
                  {step === "cart" ? (
                    <button
                      onClick={() => setStep("checkout")}
                      data-testid="cart-continue-btn"
                      className="w-full bg-brand-burgundy text-brand-cream font-bold uppercase tracking-widest text-sm py-4 rounded-xl hover:bg-brand-red transition-colors"
                    >
                      Continuar
                    </button>
                  ) : (
                    <button
                      onClick={handleSend}
                      disabled={!canSubmit}
                      data-testid="send-whatsapp-btn"
                      className="w-full bg-[#25D366] text-white font-bold uppercase tracking-widest text-sm py-4 rounded-xl flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-105 transition"
                    >
                      <WhatsAppIcon className="w-5 h-5" />
                      Enviar pedido por WhatsApp
                    </button>
                  )}
                  {step === "cart" && (
                    <button
                      onClick={clearCart}
                      className="w-full text-brand-red text-xs font-semibold mt-2.5 py-1"
                    >
                      Vaciar carrito
                    </button>
                  )}
                </footer>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
